/** One synchronous, full-parity result. U1b publishes it; U2 installs it. */
export type AppliedRow = Record<string, unknown>;
export interface AppliedRowIdentity { table: string; row_id: string }
export interface AppliedTouchedRow extends AppliedRowIdentity { row: AppliedRow; position?: AppliedRow | null; partial?: true; base?: AppliedRow; node?: AppliedRow }
export interface AppliedNewEntity {
    entity: 'creature'; entity_id: string; base_rev: number;
    base: AppliedRow; member: AppliedRow;
    pending?: true;
}
export interface AppliedNewInventory {
    entity_id: string; possess_id: string; base: AppliedRow[]; node: AppliedRow;
    /** Borrowed first-load donors; the publisher emits their scoped closure once. */
    definition_inputs?: AppliedRow;
    definition_catalog?: Readonly<Record<string, AppliedRow>>;
}
export type AppliedEntityList = readonly AppliedNewEntity[] & { readonly new_inventory?: readonly AppliedNewInventory[] };
export interface AppliedFrameIdentity {
    operation_id: string; encounter_id: string; actor_id: string; rev: number;
    member_revs: Readonly<Record<string, number>>;
}
export interface AppliedFrame extends AppliedFrameIdentity {
    kind: 'applied'; rows: readonly AppliedTouchedRow[];
    inputs?: AppliedRow;
    computed: Readonly<Record<string, AppliedRow>>;
    computed_delta?: import('../compile/action-computed-delta').ActionComputedDelta;
    new_inventory?: readonly AppliedNewInventory[];
    new_entities: readonly AppliedNewEntity[]; deletes: readonly AppliedRowIdentity[];
}
export interface AppliedCacheFrame extends AppliedFrameIdentity { kind: 'applied'; cache_ref: string }
export type AppliedFrameResult = AppliedFrame | AppliedCacheFrame;
export const APPLIED_FRAME_MAX_BYTES = 128 * 1024;
export const appliedFrameBytes = (frame: AppliedFrameResult): number => new TextEncoder().encode(JSON.stringify(frame)).byteLength;

/** Expand a decoded GM frame before any consumer adopts a newborn base. */
export function expandAppliedFrameInputs(frame: AppliedFrame, copy = false): AppliedFrame {
    const shared = frame.inputs?.new_entity_base;
    if (!shared || typeof shared !== 'object' || Array.isArray(shared)) return frame;
    // Receivers share one decode with read-only classifiers. Expansion owns only
    // the changed shells; adoption copies the actual rows when they are installed.
    if (copy) frame = { ...frame, inputs: { ...frame.inputs }, new_entities: frame.new_entities.map(entity => ({ ...entity })) };
    const inputs = shared as AppliedRow;
    for (const entity of frame.new_entities) entity.base = { ...inputs, ...entity.base };
    frame.inputs!.catalog = { ...inputs.catalog as AppliedRow, ...frame.inputs!.catalog as AppliedRow };
    delete frame.inputs!.new_entity_base;
    return frame;
}

/** Borrows held values, without cloning or mutation. The operation identity is the outbox's.
 * A reference recovers only its named saved members; it is not a Redis cache key.
 * Production producers retain full values for the bounded socket-parts lane.
 * This builder neither writes a cache nor sends a frame. */
export function buildAppliedFrame(input: Omit<AppliedFrame, 'kind'>, maxBytes = APPLIED_FRAME_MAX_BYTES): AppliedFrameResult {
    const frame: AppliedFrame = { kind: 'applied', ...input };
    if (maxBytes === Infinity || appliedFrameBytes(frame) <= maxBytes) return frame;
    const { operation_id, encounter_id, actor_id, rev, member_revs } = input;
    return { kind: 'applied', operation_id, encounter_id, actor_id, rev, member_revs,
        cache_ref: `janus:applied:${encounter_id}:${operation_id}` };
}
